/* ==========================================================================================================
   SK size guide (8 Oct 2026); in the SK SECOND SANDBOX review package since 13 Oct 2026. Styles: assets/sk-size-guide.css.
   The theme opens the size guide in a Colorbox popup (assets/theme.js, unchanged). Just before it opens, this
   script checks whether the size chart fits the screen with every column showing:
     - it fits (computers, most tablets): nothing changes, every column shows;
     - it does not (phones): the chart switches to a compact layout with tighter cells, and measurements given in
       both inches and centimetres show one unit at a time, with a cm / inches switch. Nothing is removed: the
       other unit is one tap away, and columns given in one unit only (waist to floor, sleeves) stay as they are.
       On screens narrower than about 360px a 'tight' step makes the cells smaller still.
   It reads the tables as the store wrote them (pages size-chart, dresses-size-chart, belt-size-chart); a table
   without inch/cm column pairs only gets the tighter cells. If the screen changes size while the popup is open
   (a phone turned sideways), it checks again and resizes the popup.
   ========================================================================================================== */
(function () {
  'use strict';
  if (window.skSizeGuide) return;

  var IN_RE = /(?:^|[\s(])(?:inch|inches|in)\.?\)?$/i;
  var CM_RE = /(?:^|[\s(])(?:cm|cms|centimet(?:er|re)s?)\.?\)?$/i;
  var LABELS = { cm: 'cm', in: 'inches' };
  var unit = 'cm';
  var scrollbar = null;

  var UNIT_WORD_RE = /^([\s\S]*?)(\s*\(?)(inch|inches|in|cm|cms|centimet(?:er|re)s?)(\.?\)?\s*)$/i;

  /* A cell's text as read: a line break counts as a space ("BUST<br>INCHES" reads "BUST INCHES"). */
  function text(el) {
    var out = '';
    (function walk(n) {
      for (var c = n.firstChild; c; c = c.nextSibling) {
        if (c.nodeType === 3) out += c.nodeValue;
        else if (c.nodeType === 1) {
          if (c.tagName === 'BR') out += ' ';
          else walk(c);
        }
      }
    })(el);
    return out.replace(/\s+/g, ' ').trim();
  }

  /* "BUST INCHES" -> "BUST <span class="sk-sg-unit">INCHES</span>", so the compact layout can show the unit on a
     small second line. The words are the store's; only the unit is wrapped (in the last piece of text that has
     any, so a space or line break after it does not matter). */
  function wrapUnit(cell) {
    var walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT, null), node = null, n;
    while ((n = walker.nextNode())) {
      if (/\S/.test(n.nodeValue)) node = n;
    }
    if (!node) return;
    var m = UNIT_WORD_RE.exec(node.nodeValue);
    if (!m || (m[1] && !m[2] && !/\s$/.test(m[1]))) return;
    var span = document.createElement('span');
    span.className = 'sk-sg-unit';
    span.textContent = m[2].trim() + m[3] + m[4].trim();
    node.nodeValue = m[1] ? m[1].replace(/\s+$/, '') + ' ' : '';
    node.parentNode.insertBefore(span, node.nextSibling);
  }
  function base(t, re) { return t.replace(re, '').replace(/[\s(]+$/, '').toLowerCase(); }

  function scrollbarWidth() {
    if (scrollbar === null) {
      var d = document.createElement('div');
      d.style.cssText = 'position:absolute;left:-10000px;top:0;width:100px;height:100px;overflow:scroll;';
      document.body.appendChild(d);
      scrollbar = d.offsetWidth - d.clientWidth;
      d.parentNode.removeChild(d);
    }
    return scrollbar;
  }

  /* Finds pairs of columns with the same name in inches and in cm ("BUST INCHES" next to "BUST CMS") and marks
     their cells. Returns the number of pairs; 0 leaves the table untouched: also when a cell spans part of a
     paired column or spans rows, and when a row spanning the whole table comes after the column names (a second
     chart in the same table, whose columns must not be hidden). */
  function markTable(table) {
    var grid = [], cols = 0, r, c;
    for (r = 0; r < table.rows.length; r++) {
      var at = 0, row = [];
      for (c = 0; c < table.rows[r].cells.length; c++) {
        var cell = table.rows[r].cells[c];
        if (cell.rowSpan > 1) return 0;
        var span = Math.max(1, cell.colSpan || 1);
        row.push({ el: cell, start: at, span: span });
        at += span;
      }
      cols = Math.max(cols, at);
      grid.push(row);
    }
    var head = null, headRow = -1;
    for (r = 0; r < grid.length && !head; r++) {
      if (grid[r].length >= 3 && grid[r].every(function (x) { return x.span === 1; })) {
        head = grid[r];
        headRow = r;
      }
    }
    if (!head) return 0;
    var inCols = {}, cmCols = {}, heads = [], pairs = 0;
    head.forEach(function (cell, i) {
      var t = text(cell.el);
      if (!IN_RE.test(t)) return;
      var name = base(t, IN_RE);
      /* A column called just "IN" next to one called "CM" is not taken for a pair (it could be anything). */
      if (!name && /^\(?in\.?\)?$/i.test(t)) return;
      [i + 1, i - 1].some(function (k) {
        var other = head[k];
        if (!other || inCols[other.start] || cmCols[other.start]) return false;
        var u = text(other.el);
        if (CM_RE.test(u) && base(u, CM_RE) === name) {
          inCols[cell.start] = true;
          cmCols[other.start] = true;
          heads.push(cell.el, other.el);
          pairs++;
          return true;
        }
        return false;
      });
    });
    if (!pairs) return 0;
    var marks = [];
    for (r = 0; r < grid.length; r++) {
      for (c = 0; c < grid[r].length; c++) {
        var x = grid[r][c];
        if (x.span === 1) {
          if (inCols[x.start]) marks.push([x.el, 'sk-sg-in']);
          else if (cmCols[x.start]) marks.push([x.el, 'sk-sg-cm']);
        } else if (x.start === 0 && x.span >= cols) {
          if (r > headRow) return 0;
          marks.push([x.el, 'sk-sg-span']);
        } else {
          for (var k = x.start; k < x.start + x.span; k++) {
            if (inCols[k] || cmCols[k]) return 0;
          }
        }
      }
    }
    heads.forEach(wrapUnit);
    marks.forEach(function (m) {
      m[0].classList.add(m[1]);
      if (m[1] === 'sk-sg-span') {
        m[0].setAttribute('data-sk-colspan', String(m[0].colSpan));
        m[0].setAttribute('data-sk-hidden-cols', String(pairs));
      }
    });
    return pairs;
  }

  function showUnit(chart) {
    chart.setAttribute('data-sk-unit', unit);
    var buttons = chart.querySelectorAll('.sk-sg-units__btn');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute('aria-pressed', String(buttons[i].getAttribute('data-unit') === unit));
    }
  }

  function setup(chart) {
    if (chart.hasAttribute('data-sk-sg')) return;
    chart.setAttribute('data-sk-sg', '');
    var pairs = 0, tables = chart.querySelectorAll('table');
    for (var i = 0; i < tables.length; i++) pairs += markTable(tables[i]);
    if (pairs) {
      var box = document.createElement('div');
      box.className = 'sk-sg-units';
      box.setAttribute('role', 'group');
      box.setAttribute('aria-label', 'Measurement units');
      ['cm', 'in'].forEach(function (u) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'sk-sg-units__btn no-hover'; /* no-hover: keeps the theme's hover colours off the switch */
        b.setAttribute('data-unit', u);
        b.textContent = LABELS[u];
        box.appendChild(b);
      });
      box.addEventListener('click', function (e) {
        var b = e.target.closest('.sk-sg-units__btn');
        if (!b) return;
        unit = b.getAttribute('data-unit');
        var charts = document.querySelectorAll('.size-chart[data-sk-sg]');
        for (var j = 0; j < charts.length; j++) showUnit(charts[j]);
      });
      var inner = chart.querySelector('.size-chart__inner') || chart;
      var title = inner.querySelector('h2');
      inner.insertBefore(box, title ? title.nextSibling : inner.firstChild);
    }
    showUnit(chart);
  }

  /* Layouts, tried in this order until the tables fit: 'full' (every column, as the store wrote it), 'compact'
     (tighter cells, one unit at a time) and 'tight' (smaller still, for screens narrower than about 360px). */
  var LEVELS = ['full', 'compact', 'tight'];

  function setLevel(chart, level) {
    var compact = level !== 'full';
    chart.classList.toggle('sk-sg--compact', compact);
    chart.classList.toggle('sk-sg--tight', level === 'tight');
    var spans = chart.querySelectorAll('.sk-sg-span');
    for (var i = 0; i < spans.length; i++) {
      var full = parseInt(spans[i].getAttribute('data-sk-colspan'), 10);
      var hidden = parseInt(spans[i].getAttribute('data-sk-hidden-cols'), 10);
      spans[i].colSpan = compact ? Math.max(1, full - hidden) : full;
    }
  }

  /* The window's width without its scroll bar; the stylesheet sizes the popup from it (--sk-sg-vw), so on a
     computer the popup never reaches under the page's scroll bar. */
  function setViewport(el) {
    el.style.setProperty('--sk-sg-vw', (document.documentElement.clientWidth || window.innerWidth) + 'px');
  }

  /* Room for the tables: the popup's widest content box (from the stylesheet) less a scroll bar. */
  function room() {
    var wrap = document.createElement('div');
    var probe = document.createElement('div');
    wrap.style.cssText = 'position:absolute;left:-10000px;top:0;width:5000px;visibility:hidden;';
    probe.className = 'size-chart size-chart';
    setViewport(probe);
    wrap.appendChild(probe);
    document.body.appendChild(wrap);
    var cs = getComputedStyle(probe);
    var width = probe.getBoundingClientRect().width - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    document.body.removeChild(wrap);
    return width - scrollbarWidth();
  }

  /* Narrowest width the widest table can take in the current layout, in whichever unit needs more room. */
  function narrowest(chart) {
    var tables = chart.querySelectorAll('table'), widest = 0;
    var units = chart.classList.contains('sk-sg--compact') && chart.querySelector('.sk-sg-units') ? ['cm', 'in'] : [null];
    var shown = chart.getAttribute('data-sk-unit');
    units.forEach(function (u) {
      if (u) chart.setAttribute('data-sk-unit', u);
      for (var i = 0; i < tables.length; i++) {
        var before = tables[i].style.width;
        tables[i].style.width = 'min-content';
        widest = Math.max(widest, tables[i].getBoundingClientRect().width);
        tables[i].style.width = before;
      }
    });
    if (shown) chart.setAttribute('data-sk-unit', shown);
    return widest;
  }

  /* Picks the first layout whose tables fit the popup. A hidden chart is laid out off screen for a moment. */
  function prepare(chart) {
    setup(chart);
    setViewport(chart);
    var holder = null, holderStyle = null;
    if (!chart.getClientRects().length) {
      holder = chart.parentNode;
      holderStyle = holder.getAttribute('style');
      holder.setAttribute('style', 'display:block;position:absolute;left:-10000px;top:0;width:5000px;visibility:hidden;');
    }
    var available = room(), chosen = LEVELS[LEVELS.length - 1];
    for (var i = 0; i < LEVELS.length; i++) {
      setLevel(chart, LEVELS[i]);
      if (narrowest(chart) <= available + 0.5) {
        chosen = LEVELS[i];
        break;
      }
    }
    setLevel(chart, chosen);
    if (holder) {
      if (holderStyle === null) holder.removeAttribute('style');
      else holder.setAttribute('style', holderStyle);
    }
    return chosen;
  }

  /* Before the theme's own click handler opens the popup (this listens in the capture phase). */
  var opener = null;
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('.size-chart-link');
    if (!link) return;
    var chart = document.querySelector('#size-chart-content > .size-chart');
    if (!chart) return;
    prepare(chart);
    opener = link;
  }, true);

  /* On closing, keyboard focus goes back to the Size Guide link (the theme's popup returned it nowhere). jQuery
     comes from the theme's vendor.min.js, which loads after this file, so this waits for it. */
  var closeBound = false;
  function bindClose() {
    if (closeBound || !window.jQuery) return;
    closeBound = true;
    window.jQuery(document).on('cbox_closed', function () {
      if (opener && document.querySelector('#size-chart-content > .size-chart') && document.contains(opener)) {
        try { opener.focus({ preventScroll: true }); } catch (err) { opener.focus(); }
      }
      opener = null;
    });
  }
  bindClose();
  document.addEventListener('DOMContentLoaded', bindClose);
  window.addEventListener('load', bindClose);

  /* Screen size changed while the popup is open: check again, then give the popup its new width and height. */
  var timer = null;
  window.addEventListener('resize', function () {
    clearTimeout(timer);
    timer = setTimeout(function () {
      var chart = document.querySelector('#cboxLoadedContent > .size-chart');
      var $ = window.jQuery;
      if (!chart || !$ || !$.colorbox) return;
      prepare(chart);
      chart.style.width = 'max-content';
      var width = Math.ceil(chart.getBoundingClientRect().width);
      chart.style.width = width + 'px';
      var height = Math.ceil(chart.getBoundingClientRect().height);
      chart.style.width = '';
      var frame = 0;
      ['cboxTopCenter', 'cboxBottomCenter'].forEach(function (id) {
        var el = document.getElementById(id);
        if (el) frame += el.offsetHeight;
      });
      var maxHeight = Math.round(window.innerHeight * 0.8) - frame;
      $.colorbox.resize({ innerWidth: width, innerHeight: Math.min(height, maxHeight) });
    }, 200);
  });

  function init() {
    var charts = document.querySelectorAll('.size-chart');
    for (var i = 0; i < charts.length; i++) setup(charts[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.skSizeGuide = { prepare: prepare };
})();
